import { Routes, Route, NavLink, Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { Pulse } from './modules/Pulse';
import { People } from './modules/People';
import { Deals } from './modules/Deals';
import { DealDetail } from './modules/DealDetail';
import { DealNew } from './modules/DealNew';
import { Hangs } from './modules/Hangs';
import { Vault } from './modules/Vault';
import { Login } from './modules/Login';
import { Onboarding } from './modules/Onboarding';
import { Settings } from './modules/Settings';
import { Crm } from './modules/Crm';
import { EntityDetail } from './modules/EntityDetail';
import { Accounting } from './modules/Accounting';
import { Opportunities } from './modules/Opportunities';
import { OpportunityDetail } from './modules/OpportunityDetail';
import { Admin } from './modules/Admin';
import { Trust } from './modules/Trust';
import { DealsAll } from './modules/DealsAll';
import { SubmitIntake } from './modules/SubmitIntake';
import { ProsimosSubmit } from './modules/ProsimosSubmit';
import { Portal } from './modules/Portal';
import { CommandBar } from './ui/CommandBar';
import { AgentChat } from './ui/AgentChat';
import { ToastProvider } from './ui/Toast';
import { Row } from './ui/Row';
import { KeyHint } from './ui/KeyHint';
import { AgentDot } from './ui/AgentDot';
import { Logo } from './ui/Logo';
import { NotificationBell } from './ui/NotificationBell';
import { useLocationTracker } from './api/useLocationTracker';
import { usePresenceHeartbeat } from './api/usePresenceHeartbeat';
import { AuthProvider, useAuth } from './api/useAuth';
import clsx from 'clsx';

function Shell() {
  const { friend, logout } = useAuth();
  useLocationTracker();
  usePresenceHeartbeat(!!friend);
  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="hairline-b sticky top-0 bg-bg z-30">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 h-11 md:h-12 flex items-center justify-between gap-2">
          {/* Logo */}
          <div className="flex items-center gap-1 shrink-0">
            <Logo size={18} />
            <span className="font-semibold tracking-tight text-sm hidden sm:inline">inner circle</span>
            <span className="text-[9px] tracking-[0.18em] uppercase text-muted ml-0.5 hidden md:inline">Ardacious</span>
          </div>
          {/* Nav - scrollable, snap on mobile */}
          <nav className="flex overflow-x-auto whitespace-nowrap snap-x scroll-smooth flex-1 min-w-0 no-scrollbar">
            {([
              { to: '/', label: 'pulse' },
              { to: '/people', label: 'people' },
              { to: '/crm', label: 'crm' },
              { to: '/deals', label: 'deals' },
              { to: '/opportunities', label: 'opps' },
              { to: '/trust', label: 'trust' },
              { to: '/accounting', label: 'acctg' },
              { to: '/hangs', label: 'hangs' },
              { to: '/vault', label: 'vault' },
              { to: '/settings', label: 'settings' },
              ...((friend?.role === 'founder' || friend?.role === 'admin') ? [{ to: '/admin', label: 'admin' }] : []),
            ] as Array<{ to: string; label: string }>).map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === '/'}
                className={({ isActive }) => clsx(
                  'snap-start px-2 sm:px-2.5 h-11 md:h-12 inline-flex items-center text-xs sm:text-sm transition-colors duration-[120ms] shrink-0',
                  isActive ? 'text-ink border-b-2 border-ink -mb-px' : 'text-muted hover:text-ink',
                )}>{n.label}</NavLink>
            ))}
          </nav>
          {/* Right cluster - collapse to icon-only on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <NotificationBell />
            <AgentDot active label="6 agents on" className="hidden sm:flex" />
            <KeyHint className="hidden lg:inline">⌘K</KeyHint>
            {friend && (
              <>
                <Link to="/settings" className="text-xs text-muted hover:text-ink hidden md:inline">{friend.name}</Link>
                <span className="text-xs text-muted hidden md:inline">·</span>
                <button onClick={logout} className="text-xs text-muted hover:text-ink hidden sm:inline">sign out</button>
                {/* Mobile: avatar link to settings */}
                <Link to="/settings" className="sm:hidden h-7 w-7 rounded-full bg-whisper flex items-center justify-center text-xs font-medium text-muted">
                  {friend.name?.[0]?.toUpperCase() ?? '?'}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-6 py-3 md:py-4"><Outlet /></div>
      </main>
      <footer className="hairline-t mt-8">
        <div className="max-w-[1400px] mx-auto px-3 h-10 flex items-center justify-between text-xs text-muted">
          <span>inner circle · private</span>
          <Row gap={2}><span>data residency: weur</span><span className="tnum">v0.3.0</span></Row>
        </div>
      </footer>
      <CommandBar />
      <AgentChat />
    </div>
  );
}

function Gate({ children }: { children: any }) {
  const { friend, needsOnboarding, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted text-sm">loading…</div>;
  if (!friend) return <Navigate to={`/login?return=${encodeURIComponent(loc.pathname)}`} replace />;
  if (needsOnboarding && loc.pathname !== '/onboarding') return <Navigate to="/onboarding" replace />;
  return children;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/onboarding" element={<Gate><Onboarding /></Gate>} />
          <Route path="/submit/:groupSlug" element={<SubmitIntake />} />
          <Route path="/prosimos/submit" element={<ProsimosSubmit />} />
          <Route path="/portal/:token" element={<Portal />} />
          <Route element={<Gate><Shell /></Gate>}>
            <Route index element={<Pulse />} />
            <Route path="people" element={<People />} />
            <Route path="deals" element={<Deals />} />
            <Route path="deals/new" element={<DealNew />} />
            <Route path="deals/all" element={<DealsAll />} />
            <Route path="deals/:id" element={<DealDetail />} />
            <Route path="hangs" element={<Hangs />} />
            <Route path="vault" element={<Vault />} />
            <Route path="settings" element={<Settings />} />
            <Route path="crm" element={<Crm />} />
            <Route path="crm/:entityId" element={<EntityDetail />} />
            <Route path="accounting" element={<Accounting />} />
            <Route path="accounting/ar" element={<Accounting />} />
            <Route path="accounting/ap" element={<Accounting />} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="opportunities/:id" element={<OpportunityDetail />} />
            <Route path="trust" element={<Trust />} />
            <Route path="admin" element={<Admin />} />
          </Route>
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}
